import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QuickBookingDrawer from '../components/dashboard/QuickBookingDrawer';
import WebCheckInInviteDialog from '../components/dashboard/WebCheckInInviteDialog';
import WebCheckInInviteDrawer from '../components/booking/WebCheckInInviteDrawer';
import DashboardCalendarWorkspace from '../components/dashboard/DashboardCalendarWorkspace';
import CheckOutDrawer from '../components/booking/CheckOutDrawer';
import CheckInDrawer from '../components/booking/CheckInDrawer';
import BookingDetailsDrawer from '../components/booking/BookingDetailsDrawer';
import DashboardEventBlockDrawer from '../components/dashboard/DashboardEventBlockDrawer';
import DashboardPendingTasksRail from '../components/dashboard/DashboardPendingTasksRail';
import DashboardPendingTasksPanel from '../components/dashboard/DashboardPendingTasksPanel';
import DashboardHousekeepingDrawer, { prefetchHousekeepingDrawerData } from '../components/dashboard/DashboardHousekeepingDrawer';
import MobileHomeDashboard from '../components/mobile/MobileHomeDashboard';
import MobileDashboardViewSwitcher from '../components/mobile/MobileDashboardViewSwitcher';
import MobileBookingCalendarShell from '../components/mobile/MobileBookingCalendarShell';
import { getMobileDashboardPanelSx } from '../components/layout/mobile/mobileAppTheme';
import {
  mobilePanelToDisplayView,
  readStoredMobileDashboardPanel,
  writeStoredMobileDashboardPanel,
} from '../components/mobile/mobileDashboardModes';
import useMobileView from '../hooks/useMobileView';
import { isTabletFormFactor } from '../utils/mobileView';
import useDashboardDataRevision from '../hooks/useDashboardDataRevision';
import { useWindowResizeSession } from '../hooks/useWindowResizeSession';
import { Box, Alert, AlertTitle } from '@mui/material';
import { useSyncedTheme } from '../hooks/useSyncedTheme';
import { getDashboardPageShellSx } from '../utils/dashboardSurfaceSx';
import { isQuickBookDateAllowed, readStoredGridDayCount, writeStoredGridDayCount } from '../utils/dateUtils';
import { readStoredGroupView, writeStoredGroupView } from '../utils/roomUtils';
import { fetchDashboardPendingTasksBundle, invalidateDashboardPendingTasksCache, readCachedPendingTasksBundle } from '../utils/dashboardPendingTasks';
import { resolveWalkInHousekeepingCleaningId } from '../utils/walkInHousekeeping';
import { notifyBookingMutation } from '../utils/bookingSync';
import {
  dashboardNeedsRefresh,
  readDashboardDataRevision,
  readDashboardLoadedRevision,
  shouldRefreshDashboardData,
} from '../utils/dashboardRefreshCoordinator';
import { invalidateDashboardDataCache } from '../utils/dashboardDataCache';
import {
  getDashboardMainSplitSx,
  getDashboardRoomBoardColumnSx,
  getPendingTasksRailColumnSx,
  readPendingTasksRailCollapsed,
  usePrefersReducedMotion,
} from '../utils/dashboardPendingTasksRail';
import { PERMISSIONS } from '../auth/permissions';
import usePermission from '../auth/usePermission';

/**
 * Main Dashboard Page
 * Displays booking calendar with responsive Material-UI design
 */
const normalizeBookingId = (value) => {
  const parsedId = Number(value);
  return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { can } = usePermission();
  const canManageWebCheckIn = can(PERMISSIONS.WEB_CHECKIN_MANAGE);
  const canEditBooking = can(PERMISSIONS.BOOKING_MODIFY);
  const { isCompact, isTabletNative } = useMobileView();
  const stackedPendingTasks = isTabletNative || isTabletFormFactor();
  const currentTheme = useSyncedTheme();
  const { dataRevision, isDashboardHome } = useDashboardDataRevision();
  const isMobileHome = isCompact && isDashboardHome;
  const isMobileTasksView = isMobileHome && searchParams.get('view') === 'tasks';

  const djangoContext = window.DJANGO_CONTEXT || {};
  const selectedHotel = djangoContext.selectedHotel || null;

  const [quickBookContext, setQuickBookContext] = useState(null);
  const [webCheckInContext, setWebCheckInContext] = useState(null);
  const [webCheckInInviteId, setWebCheckInInviteId] = useState(null);
  const [checkOutBookingId, setCheckOutBookingId] = useState(null);
  const [checkInBookingId, setCheckInBookingId] = useState(null);
  const [bookingDetailsId, setBookingDetailsId] = useState(null);
  const [housekeepingCleaningId, setHousekeepingCleaningId] = useState(null);
  const [eventBlockContext, setEventBlockContext] = useState(null);
  const appliedPendingRevisionRef = useRef(readDashboardLoadedRevision());
  const initialPendingTasksRef = useRef(null);
  if (initialPendingTasksRef.current === null) {
    initialPendingTasksRef.current = dashboardNeedsRefresh()
      ? null
      : readCachedPendingTasksBundle();
  }
  const initialPendingTasksBundle = initialPendingTasksRef.current;

  const [pendingCleaningByRoomId, setPendingCleaningByRoomId] = useState(
    () => initialPendingTasksBundle?.pendingCleaningByRoomId || {},
  );
  const [pendingTasksData, setPendingTasksData] = useState(
    () => initialPendingTasksBundle?.tasks || { sections: [], totalCount: 0 },
  );
  const [pendingTasksLoading, setPendingTasksLoading] = useState(() => !initialPendingTasksBundle);
  const [pendingTasksRefreshing, setPendingTasksRefreshing] = useState(false);
  const [roomBoardRefreshing, setRoomBoardRefreshing] = useState(false);
  const [boardRefreshKey, setBoardRefreshKey] = useState(0);
  const pendingTasksInitializedRef = useRef(Boolean(initialPendingTasksBundle));
  const [housekeepingError, setHousekeepingError] = useState('');
  const [housekeepingResolving, setHousekeepingResolving] = useState(false);
  const [mobilePanel, setMobilePanel] = useState(() => readStoredMobileDashboardPanel());
  const [pendingTasksCollapsed, setPendingTasksCollapsed] = useState(() => readPendingTasksRailCollapsed());
  const reducedMotion = usePrefersReducedMotion();
  const isWindowResizing = useWindowResizeSession();
  const pauseLayoutMotion = reducedMotion || isWindowResizing;

  // iPad/tablet: apply tablet-specific collapse preference (open by default)
  useEffect(() => {
    if (!stackedPendingTasks) return;
    setPendingTasksCollapsed(readPendingTasksRailCollapsed());
  }, [stackedPendingTasks]);

  const [displayView, setDisplayView] = useState('ultra');
  const [currentDate, setCurrentDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [groupView, setGroupView] = useState(() => readStoredGroupView());
  const [gridDayCount, setGridDayCount] = useState(() => readStoredGridDayCount());

  const handleQuickBook = useCallback((context) => {
    if (!isQuickBookDateAllowed(context?.checkInDate)) return;
    setQuickBookContext(context);
  }, []);

  const handleWebCheckIn = useCallback((context) => {
    if (!isQuickBookDateAllowed(context?.checkInDate)) return;
    setWebCheckInContext(context);
  }, []);

  const handleOpenWebCheckInInvite = useCallback((invite) => {
    const inviteId = Number(invite?.id);
    if (!Number.isFinite(inviteId) || inviteId <= 0) return;
    setWebCheckInInviteId(inviteId);
  }, []);

  const handleWebCheckInInviteUpdated = useCallback(() => {
    notifyBookingMutation({ type: 'web_checkin_invite_updated' });
  }, []);

  const handleCheckout = useCallback((bookingId) => {
    const safeBookingId = normalizeBookingId(bookingId);
    if (!safeBookingId) return;
    setCheckOutBookingId(safeBookingId);
  }, []);

  const handleCheckIn = useCallback((bookingId) => {
    const safeBookingId = normalizeBookingId(bookingId);
    if (!safeBookingId) return;
    setCheckInBookingId(safeBookingId);
  }, []);

  const handlePayment = useCallback((bookingId) => {
    const safeBookingId = normalizeBookingId(bookingId);
    if (!safeBookingId) return;
    setBookingDetailsId(safeBookingId);
  }, []);

  const handleBookingDetailsClose = useCallback(() => {
    setBookingDetailsId(null);
  }, []);

  const handleBookingDetailsUpdate = useCallback(() => {
    notifyBookingMutation({ type: 'booking_details_updated' });
  }, []);

  const handleCheckOutClose = useCallback(() => {
    setCheckOutBookingId(null);
  }, []);

  const handleCheckOutSuccess = useCallback(() => {
    setCheckOutBookingId(null);
    notifyBookingMutation({ type: 'checkout' });
  }, []);

  const handleCheckInClose = useCallback(() => {
    setCheckInBookingId(null);
  }, []);

  const handleCheckInSuccess = useCallback(() => {
    setCheckInBookingId(null);
    notifyBookingMutation({ type: 'checkin' });
  }, []);

  const handleHousekeepingTask = useCallback((cleaningId) => {
    const safeCleaningId = Number(cleaningId);
    if (!Number.isInteger(safeCleaningId) || safeCleaningId <= 0) return;
    setHousekeepingCleaningId(safeCleaningId);
  }, []);

  const handleHousekeepingClose = useCallback(() => {
    setHousekeepingCleaningId(null);
  }, []);

  const handleHousekeepingSuccess = useCallback(({ roomId } = {}) => {
    setHousekeepingCleaningId(null);
    if (roomId != null) {
      setPendingCleaningByRoomId((prev) => {
        const next = { ...prev };
        delete next[String(roomId)];
        return next;
      });
    }
    notifyBookingMutation({ type: 'housekeeping' });
  }, []);

  const loadPendingTasksBundle = useCallback(async ({ forceRefresh = false, silent = false } = {}) => {
    if (forceRefresh) {
      setPendingTasksRefreshing(true);
      invalidateDashboardPendingTasksCache();
    } else if (!silent && !pendingTasksInitializedRef.current) {
      setPendingTasksLoading(true);
    }

    try {
      const bundle = await fetchDashboardPendingTasksBundle({ forceRefresh });
      setPendingTasksData(bundle.tasks);
      setPendingCleaningByRoomId(bundle.pendingCleaningByRoomId || {});
      prefetchHousekeepingDrawerData();
    } catch {
      if (!silent) {
        setPendingTasksData({ sections: [], totalCount: 0 });
        setPendingCleaningByRoomId({});
      }
    } finally {
      pendingTasksInitializedRef.current = true;
      setPendingTasksLoading(false);
      setPendingTasksRefreshing(false);
    }
  }, []);

  const handleRefreshRoomBoard = useCallback(() => {
    setRoomBoardRefreshing(true);
    invalidateDashboardDataCache('calendar:');
    setBoardRefreshKey((key) => key + 1);
  }, []);

  const handleBoardRefreshSettled = useCallback(() => {
    setRoomBoardRefreshing(false);
  }, []);

  const handleDirtyRoomHousekeeping = useCallback(async (room) => {
    if (!room?.id || housekeepingResolving) return;
    setHousekeepingResolving(true);
    setHousekeepingError('');
    try {
      const cleaningId = await resolveWalkInHousekeepingCleaningId(room.id, pendingCleaningByRoomId);
      setHousekeepingCleaningId(cleaningId);
      await loadPendingTasksBundle({ forceRefresh: true, silent: true });
    } catch (err) {
      const message = err?.error || err?.detail || err?.message || 'Could not open housekeeping for this room.';
      setHousekeepingError(message);
    } finally {
      setHousekeepingResolving(false);
    }
  }, [housekeepingResolving, pendingCleaningByRoomId, loadPendingTasksBundle]);

  const handleHousekeepingError = useCallback((message) => {
    setHousekeepingError(message || 'Failed to mark room as clean.');
    notifyBookingMutation({ type: 'housekeeping_error' });
    loadPendingTasksBundle({ forceRefresh: true });
  }, [loadPendingTasksBundle]);

  useEffect(() => {
    if (!isDashboardHome) return undefined;

    const needsRefresh = shouldRefreshDashboardData(appliedPendingRevisionRef.current);
    if (needsRefresh) {
      invalidateDashboardPendingTasksCache();
      loadPendingTasksBundle({ forceRefresh: true }).then(() => {
        appliedPendingRevisionRef.current = readDashboardDataRevision();
      });
      return undefined;
    }

    if (!pendingTasksInitializedRef.current) {
      loadPendingTasksBundle({ forceRefresh: false, silent: Boolean(initialPendingTasksBundle) });
      return undefined;
    }

    return undefined;
  }, [dataRevision, isDashboardHome, initialPendingTasksBundle, loadPendingTasksBundle]);

  const handleModifyBooking = useCallback((bookingId) => {
    if (!canEditBooking) return;
    const safeBookingId = normalizeBookingId(bookingId);
    if (!safeBookingId) return;
    setCheckOutBookingId(null);
    navigate(`/booking/edit/${safeBookingId}/`);
  }, [canEditBooking, navigate]);

  const handleModifyBookingFromCheckIn = useCallback((bookingId) => {
    if (!canEditBooking) return;
    const safeBookingId = normalizeBookingId(bookingId);
    if (!safeBookingId) return;
    setCheckInBookingId(null);
    navigate(`/booking/edit/${safeBookingId}/`);
  }, [canEditBooking, navigate]);

  const handleEventBlock = useCallback((context) => {
    if (!context?.roomTypeId) return;
    setEventBlockContext(context);
  }, []);

  const handleEventBlockClose = useCallback(() => {
    setEventBlockContext(null);
  }, []);

  const handleEventBlockSuccess = useCallback(() => {
    setEventBlockContext(null);
    notifyBookingMutation({ type: 'event_block' });
  }, []);

  const resetToToday = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setCurrentDate(today);
  }, []);

  const handleViewChange = useCallback((view) => {
    setDisplayView(view);
    resetToToday();
  }, [resetToToday]);

  const handleDateChange = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  const handleGridDayCountChange = useCallback((count) => {
    setGridDayCount(count);
    writeStoredGridDayCount(count);
  }, []);

  const handleGroupViewChange = useCallback((view) => {
    setGroupView(view);
    writeStoredGroupView(view);
  }, []);

  const handleMobilePanelChange = useCallback((panelId) => {
    setMobilePanel(panelId);
    writeStoredMobileDashboardPanel(panelId);
    if (panelId !== 'overview') {
      setDisplayView(mobilePanelToDisplayView(panelId));
      resetToToday();
    }
  }, [resetToToday]);

  const mobileCalendarView = mobilePanelToDisplayView(mobilePanel);

  return (
    <Box
      className="rn-dashboard-enter"
      sx={isMobileHome ? { width: '100%', ...getMobileDashboardPanelSx() } : getDashboardPageShellSx(currentTheme)}
    >
      {isMobileHome ? (
        <>
          {!isMobileTasksView ? (
            <Box className="rn-dashboard-part" style={{ '--dashboard-delay': '0ms' }} sx={{ px: 1.25, pt: 0.35, pb: 0.45, flexShrink: 0 }}>
              <MobileDashboardViewSwitcher value={mobilePanel} onChange={handleMobilePanelChange} />
            </Box>
          ) : null}

          {isMobileTasksView ? (
            <Box
              className="rn-dashboard-part"
              style={{ '--dashboard-delay': '80ms' }}
              sx={{
                ...getMobileDashboardPanelSx(),
                px: 1.25,
                pb: 1,
              }}
            >
              <DashboardPendingTasksPanel
                themeMode={currentTheme}
                refreshKey={dataRevision}
                tasksData={pendingTasksData}
                tasksLoading={pendingTasksLoading}
                tasksRefreshing={pendingTasksRefreshing}
                onRefreshTasks={() => loadPendingTasksBundle({ forceRefresh: true })}
                onCheckout={handleCheckout}
                onCheckIn={handleCheckIn}
                onPayment={handlePayment}
                onHousekeeping={handleHousekeepingTask}
                onOpenWebCheckInInvite={handleOpenWebCheckInInvite}
              />
            </Box>
          ) : mobilePanel === 'overview' ? (
            <Box className="rn-dashboard-part" style={{ '--dashboard-delay': '80ms' }} sx={getMobileDashboardPanelSx()}>
              <MobileHomeDashboard />
            </Box>
          ) : (
            <>
              {!selectedHotel && (
                <Box sx={{ px: 1.25, pb: 0.75, flexShrink: 0 }}>
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    <AlertTitle>No hotel selected</AlertTitle>
                    Select a hotel from the header to load the booking calendar.
                  </Alert>
                </Box>
              )}
              <Box className="rn-dashboard-part" style={{ '--dashboard-delay': '160ms' }} sx={getMobileDashboardPanelSx()}>
                <MobileBookingCalendarShell
                  displayView={mobileCalendarView}
                  currentDate={currentDate}
                  onDateChange={handleDateChange}
                  groupView={groupView}
                  onGroupViewChange={handleGroupViewChange}
                  themeMode={currentTheme}
                  onQuickBook={handleQuickBook}
                  onWebCheckIn={canManageWebCheckIn ? handleWebCheckIn : undefined}
                  onOpenWebCheckInInvite={canManageWebCheckIn ? handleOpenWebCheckInInvite : undefined}
                  quickBookContext={quickBookContext}
                  onQuickBookClose={() => setQuickBookContext(null)}
                  onQuickBookSuccess={() => {
                    setQuickBookContext(null);
                  }}
                  onCheckout={handleCheckout}
                  onCheckIn={handleCheckIn}
                  onEventBlock={handleEventBlock}
                  onHousekeeping={handleHousekeepingTask}
                  onDirtyRoomHousekeeping={handleDirtyRoomHousekeeping}
                  pendingCleaningByRoomId={pendingCleaningByRoomId}
                  dataRevision={dataRevision}
                />
              </Box>
            </>
          )}
        </>
      ) : (
        <>
      {!selectedHotel && (
        <Box sx={{ flexShrink: 0, mb: 3 }}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <AlertTitle>No hotel selected</AlertTitle>
            Please select a hotel from the{' '}
            <a href="/onboarding/landing/" style={{ color: 'inherit', textDecoration: 'underline' }}>
              landing page
            </a>
            .
          </Alert>
        </Box>
      )}

      <Box
        sx={{
          ...getDashboardMainSplitSx({
            collapsed: pendingTasksCollapsed,
            reducedMotion: pauseLayoutMotion,
            stackedLayout: stackedPendingTasks,
          }),
        }}
      >
        <Box className="rn-dashboard-part" style={{ '--dashboard-delay': '80ms' }} sx={getDashboardRoomBoardColumnSx(pauseLayoutMotion, stackedPendingTasks)}>
          <DashboardCalendarWorkspace
            themeMode={currentTheme}
            displayView={displayView}
            currentDate={currentDate}
            onViewChange={handleViewChange}
            onDateChange={handleDateChange}
            groupView={groupView}
            onGroupViewChange={handleGroupViewChange}
            gridDayCount={gridDayCount}
            onGridDayCountChange={handleGridDayCountChange}
            onQuickBook={handleQuickBook}
            onWebCheckIn={canManageWebCheckIn ? handleWebCheckIn : undefined}
            onOpenWebCheckInInvite={canManageWebCheckIn ? handleOpenWebCheckInInvite : undefined}
            onCheckout={handleCheckout}
            onCheckIn={handleCheckIn}
            onEventBlock={handleEventBlock}
            onHousekeeping={handleHousekeepingTask}
            onDirtyRoomHousekeeping={handleDirtyRoomHousekeeping}
            pendingCleaningByRoomId={pendingCleaningByRoomId}
            dataRevision={dataRevision}
            boardRefreshKey={boardRefreshKey}
            boardRefreshing={roomBoardRefreshing}
            onRefreshBoard={handleRefreshRoomBoard}
            onBoardRefreshSettled={handleBoardRefreshSettled}
          />
        </Box>
        <Box className="rn-dashboard-part" style={{ '--dashboard-delay': '160ms' }} sx={getPendingTasksRailColumnSx({ collapsed: pendingTasksCollapsed, reducedMotion: pauseLayoutMotion, stackedLayout: stackedPendingTasks })}>
          <DashboardPendingTasksRail
          themeMode={currentTheme}
          refreshKey={dataRevision}
          tasksData={pendingTasksData}
          tasksLoading={pendingTasksLoading}
          tasksRefreshing={pendingTasksRefreshing}
          onRefreshTasks={() => loadPendingTasksBundle({ forceRefresh: true })}
          onCheckout={handleCheckout}
          onCheckIn={handleCheckIn}
          onPayment={handlePayment}
          onHousekeeping={handleHousekeepingTask}
          onOpenWebCheckInInvite={handleOpenWebCheckInInvite}
          collapsed={pendingTasksCollapsed}
          onCollapsedChange={setPendingTasksCollapsed}
          stackedLayout={stackedPendingTasks}
          />
        </Box>
      </Box>

      <QuickBookingDrawer
        open={Boolean(quickBookContext)}
        context={quickBookContext}
        onClose={() => setQuickBookContext(null)}
        onSuccess={() => {
          setQuickBookContext(null);
        }}
        themeMode={currentTheme}
      />
        </>
      )}

      {canManageWebCheckIn && (
        <WebCheckInInviteDialog
          open={Boolean(webCheckInContext)}
          context={webCheckInContext}
          onClose={() => setWebCheckInContext(null)}
          onSuccess={() => {
            setWebCheckInContext(null);
            notifyBookingMutation({ type: 'web_checkin_invite_sent' });
          }}
          themeMode={currentTheme}
        />
      )}

      {canManageWebCheckIn && (
        <WebCheckInInviteDrawer
          open={Boolean(webCheckInInviteId)}
          inviteId={webCheckInInviteId}
          onClose={() => setWebCheckInInviteId(null)}
          onUpdated={handleWebCheckInInviteUpdated}
        />
      )}

      <CheckOutDrawer
        bookingId={checkOutBookingId}
        open={Boolean(checkOutBookingId)}
        onClose={handleCheckOutClose}
        onCheckOutSuccess={handleCheckOutSuccess}
        onModifyBooking={canEditBooking ? handleModifyBooking : undefined}
      />

      <CheckInDrawer
        bookingId={checkInBookingId}
        open={Boolean(checkInBookingId)}
        onClose={handleCheckInClose}
        onCheckInSuccess={handleCheckInSuccess}
        onModifyBooking={canEditBooking ? handleModifyBookingFromCheckIn : undefined}
      />

      <BookingDetailsDrawer
        bookingId={bookingDetailsId}
        open={Boolean(bookingDetailsId)}
        onClose={handleBookingDetailsClose}
        onStatusUpdate={handleBookingDetailsUpdate}
      />

      <DashboardEventBlockDrawer
        open={Boolean(eventBlockContext)}
        context={eventBlockContext}
        onClose={handleEventBlockClose}
        onSuccess={handleEventBlockSuccess}
      />

      <DashboardHousekeepingDrawer
        cleaningId={housekeepingCleaningId}
        open={Boolean(housekeepingCleaningId)}
        onClose={handleHousekeepingClose}
        onSuccess={handleHousekeepingSuccess}
        onError={handleHousekeepingError}
        themeMode={currentTheme}
      />

      {housekeepingError ? (
        <Alert
          severity="error"
          onClose={() => setHousekeepingError('')}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: (theme) => theme.zIndex.snackbar,
            maxWidth: 480,
            width: 'calc(100% - 32px)',
          }}
        >
          {housekeepingError}
        </Alert>
      ) : null}
    </Box>
  );
};

export default DashboardPage;
